import { styled } from "styled-components";

import {
    colorCSS,
    fontSizes,
    spacing,
    zIndex,
    BackgroundField,
    getContainerWidth,
    theme,
    BIG_NAV_PX_HEIGHT,
    SMALL_NAV_PX_HEIGHT,
    SUB_NAV_PX_HEIGHT
} from "../../styles";
import { redundantCssFromKebab } from "../../styles/redundant-css";

export const Guide = styled.div<{
    background?: BackgroundField[];
    headerOffset: boolean;
}>(
    {
        counterReset: "listcounter -1"
    },
    ({ headerOffset }) =>
        headerOffset &&
        `padding-top: ${SMALL_NAV_PX_HEIGHT + SUB_NAV_PX_HEIGHT}px;`,
    {
        "position": "relative",

        "aside": {
            background: colorCSS("N0"),
            boxShadow: `0 0.5rem 0.75rem 0 ${colorCSS("N900", 0.15)}`,
            left: 0,
            position: "fixed",
            right: 0,
            top: `${SMALL_NAV_PX_HEIGHT}px`,
            zIndex: zIndex.subNav,

            button: {
                backgroundColor: colorCSS("N0"),
                border: 0,
                color: colorCSS("P500"),
                height: `${SUB_NAV_PX_HEIGHT}px`,
                overflow: "hidden",
                padding: "0 3rem 0 1rem",
                position: "relative",
                textAlign: "left",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                width: "100%",

                svg: {
                    position: "absolute",
                    right: "1rem",
                    top: "22px",
                    transition: "0.2s transform ease"
                }
            },

            a: {
                color: "inherit",
                display: "block",
                textDecoration: "none"
            }
        },

        "main": {
            nav: {
                display: "flex",
                flexDirection: "row-reverse",
                padding: `${spacing["cozy"]} 1rem ${spacing["outerspace"]}`
            }
        },

        "&:after": {
            backgroundColor: "black",
            content: '""',
            opacity: 0,
            position: "fixed",
            transition: "0.2s opacity ease",
            zIndex: zIndex.subNavShroud
        },
        [`&[data-nav-open="true"]`]: {
            "aside": {
                "button svg": {
                    transform: "rotate(180deg)"
                }
            },

            "&:after": {
                bottom: 0,
                opacity: 0.4,
                left: 0,
                top: 0,
                right: 0
            }
        },
        "@media (min-width: 992px)": {
            "display": "flex",
            "minHeight": "100vh",
            "paddingTop": `${BIG_NAV_PX_HEIGHT}px`,

            "aside": {
                boxShadow: `0.5rem 0 0.75rem 0 ${colorCSS("N900", 0.15)}`,
                clipPath: "inset(0 -1rem 0 0)",
                minHeight: "100%",
                padding: `${spacing["cozy"]} 0 0`,
                position: "static",
                width: `calc( ${getContainerWidth(
                    "lg",
                    3
                )} + ((100vw - ${getContainerWidth("lg")}) / 2) )`,
                zIndex: 0,

                button: {
                    display: "none"
                }
            },

            "main": {
                "flexGrow": 1,

                "> section > container > section > div, > nav, > .subSections > section > div":
                    {
                        marginLeft: getContainerWidth("lg", 1),
                        width: getContainerWidth("lg", 8)
                    }
            },

            '&[data-nav-open="true"]:after': {
                bottom: "auto",
                left: "auto",
                opacity: 0,
                right: "auto",
                top: "auto"
            }
        },
        "@media (min-width: 1200px)": {
            "aside": {
                width: `calc( ${getContainerWidth(
                    "xl",
                    3
                )} + ((100vw - ${getContainerWidth("xl")}) / 2) )`
            },

            "main > section > div, main > nav, main > .subSections > section > div":
                {
                    marginLeft: getContainerWidth("xl", 1),
                    width: getContainerWidth("xl", 8)
                }
        }
    }
);

export const SubSections = styled.div`
    ${redundantCssFromKebab("padding", `0 0 ${spacing["outerspace"]}`)}
`;

export const Nav = styled.nav({
    "backgroundColor": colorCSS("N10"),
    "height": 0,
    "opacity": 0,
    "overflow": "hidden",
    "transform": "translateY(-1rem)",
    "transition": "0.2s all ease",

    "h5": {
        color: colorCSS("N500"),
        fontWeight: 400,
        margin: "1rem 0 0 1rem"
    },

    '&[data-open="true"]': {
        height: "auto",
        maxHeight: `calc(100vh - ${SUB_NAV_PX_HEIGHT + SMALL_NAV_PX_HEIGHT}px)`,
        overflow: "auto",
        opacity: 1,
        paddingBottom: "0.5rem",
        transform: "translateY(0)"
    },

    "> a": {
        "counterIncrement": "listcounter",
        "padding": "0.5rem 1rem",
        "position": "relative",

        "&.active": {
            backgroundColor: colorCSS("P50")
        },

        "&:first-child": {
            "paddingLeft": "1rem",

            "&::before": {
                visibility: "hidden"
            }
        }
    },

    '&[data-numbered="true"] > a': {
        "paddingLeft": "2.5rem",

        "&::before": {
            content: "counter(listcounter)",
            position: "absolute"
        },

        "&:first-child": {
            paddingLeft: "1rem"
        }
    },

    "nav": {
        display: "none"
    },
    "@media (min-width: 992px)": {
        "backgroundColor": "transparent",
        "display": "block",
        "fontSize": fontSizes.sm,
        "height": "auto",
        "margin": "0 0 0 auto",
        "padding": "2rem 2rem 2rem 1rem",
        "width": getContainerWidth("lg", 3),
        "opacity": 1,
        "overflow": "auto",
        "transform": "none",

        "h5": {
            marginLeft: 0
        },

        "> a": {
            "marginTop": "0.5rem",
            "padding": "0.5rem 0",

            "&:first-child": {
                color: "inherit !important",
                fontFamily: theme.fontFamily.display,
                fontSize: fontSizes.lg,
                fontWeight: 500,
                lineHeight: 1.33333333,
                marginTop: 0,
                paddingLeft: 0,
                paddingTop: 0
            },

            "&:hover": {
                color: colorCSS("P400")
            },

            "&::before": {
                left: 0
            },

            "&[aria-current]": {
                backgroundColor: "transparent",
                color: colorCSS("P400"),
                fontWeight: 500
            }
        },

        '&[data-numbered="true"] > a': {
            "paddingLeft": "1.5rem",

            "&:first-child": {
                paddingLeft: 0
            }
        },

        '&[data-first-chapter="false"] > a:first-child::before': {
            backgroundImage:
                'url(\'data:image/svg+xml;utf8,<svg fill="none" height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg"><path fill="currentColor" d="m14.707 6.29289c.3626.36263.3885.93445.0777 1.32697l-.0777.08725-4.2928 4.29219 4.2928 4.2936c.3626.3626.3885.9344.0777 1.327l-.0777.0872c-.3626.3626-.9345.3885-1.327.0777l-.0872-.0777-5.00003-5c-.36263-.3626-.38853-.9345-.07771-1.327l.07771-.0872 5.00003-5.00001c.3905-.39052 1.0236-.39052 1.4142 0z"/></svg>\')',
            backgroundPosition: "center center",
            backgroundRepeat: "no-repeat",
            backgroundSize: "contain",
            color: colorCSS("N900"),
            content: "''",
            height: "1.25rem",
            left: "-1.25rem",
            position: "absolute",
            top: "7px",
            visibility: "visible",
            width: "1.25rem"
        },

        "nav": {
            display: "block",

            a: {
                "borderLeft": "4px solid transparent",
                "padding": "0.5rem 0 0.5rem 1.25rem",

                "&.active, &:hover": {
                    color: colorCSS("P400")
                },

                "&.active": {
                    borderColor: colorCSS("P400")
                }
            }
        }
    },
    "@media (min-width: 1200px)": {
        width: getContainerWidth("xl", 3)
    }
});
